<template>
  <div class="blocos-list">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>{{ titulo }}</span>
        <v-btn
          v-if="mostrarBotaoVerMais"
          variant="text"
          color="primary"
          :to="{ name: 'blocks' }"
          size="small"
        >
          Ver Todos
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="pa-0">
        <v-data-table
          :headers="cabecalhos"
          :items="blocos"
          :loading="carregando"
          loading-text="Carregando blocos..."
          no-data-text="Nenhum bloco encontrado"
          class="blocos-table"
          hover
          density="compact"
          :items-per-page="itemsPorPagina"
          :hide-default-footer="esconderPaginacao"
        >
          <!-- Número do Bloco -->
          <template v-slot:item.number="{ item }">
            <router-link 
              :to="{ name: 'block-details', params: { id: item.number } }"
              class="font-weight-medium text-primary text-decoration-none"
            >
              {{ item.number }}
            </router-link>
          </template>
          
          <!-- Hash do Bloco -->
          <template v-slot:item.hash="{ item }">
            <div class="text-truncate" style="max-width: 120px;">
              {{ formatarHash(item.hash) }}
            </div>
          </template>
          
          <!-- Previous Hash -->
          <template v-slot:item.previousHash="{ item }">
            <div class="text-truncate" style="max-width: 100px;">
              {{ item.previousHash ? formatarHash(item.previousHash) : '-' }}
            </div>
          </template>
          
          <!-- State Root -->
          <template v-slot:item.stateRoot="{ item }">
            <div class="text-truncate" style="max-width: 100px;">
              {{ item.stateRoot ? formatarHash(item.stateRoot) : '-' }}
            </div>
          </template>
          
          <!-- Timestamp -->
          <template v-slot:item.timestamp="{ item }">
            <div class="d-flex align-center">
              <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <span v-bind="props">{{ tempoRelativo(item.timestamp) }}</span>
                </template>
                {{ formatarData(item.timestamp) }}
              </v-tooltip>
            </div>
          </template>
          
          <!-- Validador -->
          <template v-slot:item.validator="{ item }">
            <router-link 
              :to="{ name: 'validator-details', params: { id: item.validator } }"
              class="text-decoration-none"
            >
              <div class="text-truncate" style="max-width: 120px;">
                {{ formatarHash(item.validator) }}
              </div>
            </router-link>
          </template>
          
          <!-- Número de Transações -->
          <template v-slot:item.transactionCount="{ item }">
            <v-chip
              size="small"
              :color="item.transactionCount > 0 ? 'primary' : 'grey'"
              variant="outlined"
              class="font-weight-medium"
            >
              {{ item.transactionCount }}
            </v-chip>
          </template>
          
          <!-- Tamanho -->
          <template v-slot:item.size="{ item }">
            {{ formatarBytes(item.size) }}
          </template>
          
          <!-- Gas -->
          <template v-slot:item.gasUsed="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div v-bind="props">
                  {{ ((item.gasUsed / item.gasLimit) * 100).toFixed(1) }}%
                </div>
              </template>
              {{ formatarNumeroGrande(item.gasUsed) }} / {{ formatarNumeroGrande(item.gasLimit) }}
            </v-tooltip>
          </template>
          
          <!-- Ações -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-btn
                icon
                variant="text"
                density="comfortable"
                size="small"
                :to="{ name: 'block-details', params: { id: item.number } }"
              >
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
            </div>
          </template>
          
          <!-- Carregando -->
          <template v-slot:loading>
            <v-skeleton-loader
              v-for="n in 5"
              :key="n"
              type="table-row"
              class="my-2"
            ></v-skeleton-loader>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { formatarHash, formatarData, tempoRelativo, formatarBytes, formatarNumeroGrande } from '@/services/utilidades'

export default {
  name: 'BlocosList',
  props: {
    titulo: {
      type: String,
      default: 'Blocos Recentes'
    },
    blocos: {
      type: Array,
      default: () => []
    },
    carregando: {
      type: Boolean,
      default: false
    },
    esconderPaginacao: {
      type: Boolean,
      default: false
    },
    mostrarBotaoVerMais: {
      type: Boolean,
      default: true
    },
    mostrarColunas: {
      type: Array,
      default: () => ['number', 'hash', 'previousHash', 'stateRoot', 'timestamp', 'transactionCount', 'validator', 'gasUsed', 'actions']
    },
    itemsPorPagina: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      todasColunas: [
        { 
          title: 'Altura', 
          key: 'number', 
          sortable: true,
          align: 'start',
          width: '80px'
        },
        { 
          title: 'Hash', 
          key: 'hash', 
          sortable: false, 
          align: 'start',
          width: '140px'
        },
        { 
          title: 'Prev Hash', 
          key: 'previousHash', 
          sortable: false, 
          align: 'start',
          width: '120px'
        },
        { 
          title: 'State Root', 
          key: 'stateRoot', 
          sortable: false, 
          align: 'start',
          width: '120px'
        },
        { 
          title: 'Tempo', 
          key: 'timestamp', 
          sortable: true,
          align: 'start',
          width: '120px'
        },
        { 
          title: 'Validador', 
          key: 'validator', 
          sortable: false,
          align: 'start',
          width: '140px'
        },
        { 
          title: 'Txs', 
          key: 'transactionCount', 
          sortable: true,
          align: 'center',
          width: '80px'
        },
        { 
          title: 'Tamanho', 
          key: 'size', 
          sortable: true,
          align: 'end',
          width: '100px'
        },
        { 
          title: 'Gas Usado', 
          key: 'gasUsed', 
          sortable: true,
          align: 'end',
          width: '100px'
        },
        { 
          title: '', 
          key: 'actions', 
          sortable: false,
          align: 'end',
          width: '50px'
        }
      ],
    }
  },
  computed: {
    cabecalhos() {
      return this.todasColunas.filter(col => this.mostrarColunas.includes(col.key))
    }
  },
  methods: {
    formatarHash,
    formatarData,
    tempoRelativo,
    formatarBytes,
    formatarNumeroGrande
  }
}
</script>

<style scoped>
.blocos-table {
  width: 100%;
}
</style>
